// Bouton « Exporter » commun aux listes du radar : Excel ou CSV, puis une ligne au journal des exports
// (`log_export`). Réservé aux administrateurs (règle `export` de canAccess).
import { Download } from "lucide-react";
import { useCanAccess, useNotify, useTranslate } from "ra-core";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import { logExport, type ExportFormat as Format } from "./exportLog";
import { toCsv, type Cell } from "./projectExport";

export type ExportTable = { headers: string[]; rows: Cell[][] };

const save = (blob: Blob, fileName: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
};

const toXlsx = async ({ headers, rows }: ExportTable, sheet: string) => {
  // Chargée à la demande : la bibliothèque n'alourdit pas le premier affichage.
  const { default: writeXlsxFile } = await import("write-excel-file/browser");
  const header = headers.map((value) => ({
    value,
    fontWeight: "bold" as const,
  }));
  const body = rows.map((row) =>
    row.map((value) =>
      value == null
        ? null
        : typeof value === "number"
          ? { value, type: Number, format: "#,##0" }
          : { value, type: String },
    ),
  );
  return writeXlsxFile([header, ...body], {
    sheet,
    stickyRowsCount: 1,
    columns: headers.map((_, i) => ({ width: i === 1 ? 50 : 18 })),
  }).toBlob();
};

export const ExportMenu = ({
  resource,
  sheet,
  file,
  filters,
  disabled,
  build,
}: {
  resource: "projects" | "deals";
  // Nom de la feuille Excel et début du nom de fichier.
  sheet: string;
  file: string;
  filters: Record<string, unknown>;
  disabled?: boolean;
  build: () => Promise<ExportTable>;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const [busy, setBusy] = useState(false);
  const { canAccess } = useCanAccess({ resource, action: "export" });

  const run = async (format: Format) => {
    setBusy(true);
    try {
      const table = await build();
      const name = `${file}-${new Date().toISOString().slice(0, 10)}.${format}`;
      save(
        format === "csv"
          ? new Blob([toCsv(table.headers, table.rows)], {
              type: "text/csv;charset=utf-8",
            })
          : await toXlsx(table, sheet),
        name,
      );
      await logExport(resource, format, table.rows.length, filters);
    } catch (error) {
      // Les erreurs de Supabase sont des objets avec un message, pas des Error.
      const message =
        error instanceof Error
          ? error.message
          : ((error as { message?: string })?.message ?? String(error));
      notify(message, {
        type: "error",
      });
    } finally {
      setBusy(false);
    }
  };

  if (!canAccess) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="cursor-pointer"
          disabled={busy || disabled}
        >
          <Download />
          {translate("ra.action.export")}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onSelect={() => run("xlsx")}>
          {translate("resources.projects.export.xlsx")}
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => run("csv")}>
          {translate("resources.projects.export.csv")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
